import _ from 'lodash';
import classnames from 'classnames';
import { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import * as api from '../../../../api';
import * as logger from '../../../../logger';
import * as store from '../../../../reducers/store';
import * as rootReducer from '../../../../reducers/root';

import CommentModal from '../CommentModal';
import ConfirmationPage from '../ConfirmationPage';
import ScheduleAppointment from '../../../../components/ScheduleAppointment';
import Filters, {
  AppointmentFilters,
} from '../../../../components/ScheduleAppointment/Filters';
import { InfoMessage } from '../../../../components/InfoMessage';

import styles from '../index.module.css';
import defaultStyles from '../../../../styles/formSetStyles.module.css';
import {
  formatString,
  getMaybeDaysFromTimingWithUnit,
  retrieveReturnToClinicStartEndDays,
} from '../../../../components/utils';
import {
  SchedulingConfig,
  SelectedAppointment,
  applyTimeFilters,
  isSelectedAppointmentBooked,
} from '../../../../components/ScheduleAppointment/AppointmentDisplay/common';
import { getLocallySortedSlots } from '../../../../components/ScheduleAppointment/AppointmentDisplay/sorting';
import {
  errorHandleGeoLocation,
  getSortBy,
  SchedulingDispatchProps,
  SchedulingStateProps,
} from '../../../../components/ScheduleAppointment/utils';
import { getCoordinates } from '../../../../utils/location';
import { verifyRequest } from '../../../../utils';

import { Alert, Loader } from '@vivaahealth/design-system';

type StateProps = SchedulingStateProps & {
  ehrProviderInformation: api.User.EhrProviderInformationPractice[] | null;
  selectedProviderId: string | null;
};

type DispatchProps = SchedulingDispatchProps & {
  setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) => void;
};

interface Props extends DispatchProps, StateProps {
  fieldConfig: SchedulingConfig;
  selectedAppointment?: SelectedAppointment;
  navigateToNextPage: () => Promise<void>;
  onAppointmentSelected: (
    timeSlot: api.EhrTimeSlot.EhrTimeSlot,
    seriesAppointmentCount?: number,
  ) => Promise<{ navigateToConfirmation: boolean }>;
  practice?: api.Practice.Practice;
  encounter?: api.Encounter.Encounter;
  appointmentNumber?: number;
  provisionalPatientId?: string;
}

enum Page {
  Loading = 'LOADING',
  Selecting = 'SELECTING',
  Booking = 'BOOKING',
  Confirmation = 'CONFIRMATION',
}

export const DEFAULT_DISPLAY_BY_SORT: Record<
  api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy,
  api.EhrTimeSlot.Sorting
> = {
  [api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice]:
    api.EhrTimeSlot.Sorting.AvailabilityPractice,
  [api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Provider]:
    api.EhrTimeSlot.Sorting.AvailabilityProvider,
};

const DEFAULT_DAYS_ALLOWED_START = 2;
const DEFAULT_DAYS_ALLOWED_END = 367;
const DEFAULT_MAX_CHAR_LENGTH = 100;
const DEFAULT_SORT_BY = api.EhrTimeSlot.Sorting.AvailabilityProvider;

const AppointmentPage: React.FC<Props> = ({
  selectedAppointment,
  fieldConfig,
  navigateToNextPage,
  practice,
  encounter,
  setCoordinates,
  coordinates,
  onAppointmentSelected,
  ehrProviderInformation,
  selectedProviderId,
  setOnCaptchaSuccess,
  appointmentNumber,
  provisionalPatientId,
}) => {
  const { t } = useTranslation();
  const {
    previsitFormFieldId,
    label,
    scheduleAppointmentDisplayBy,
    scheduleAppointmentSortBy,
    scheduleAppointmentStartTime,
    scheduleAppointmentEndTime,
    scheduleAppointmentCommentEnabled,
    customConfigs,
  } = fieldConfig;
  const [isMultiView, setIsMultiView] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [page, setPage] = useState(Page.Loading);
  const [filters, setFilters] = useState<AppointmentFilters>({
    sort:
      (scheduleAppointmentDisplayBy &&
        DEFAULT_DISPLAY_BY_SORT[scheduleAppointmentDisplayBy]) ??
      DEFAULT_SORT_BY,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] =
    useState<api.EhrTimeSlot.AvailableEhrTimeSlotResponse>();
  const [daysAllowedFrom, setDaysAllowedFrom] = useState(
    DEFAULT_DAYS_ALLOWED_START,
  );
  const [daysAllowedTo, setDaysAllowedTo] = useState(DEFAULT_DAYS_ALLOWED_END);
  const [selectedTimeSlot, setSelectedTimeSlot] =
    useState<api.EhrTimeSlot.EhrTimeSlot | null>(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [order, setOrder] = useState<api.PatientOrder.PatientOrder>();
  const [currentPatientOrder, setCurrentPatientOrder] =
    useState<api.EhrTimeSlot.EhrTimeSlotPatientOrder>();
  const [seriesAppointmentCount, setSeriesAppointmentCount] =
    useState<number>();
  const history = useHistory();

  const isSeriesAppointmentScheduling =
    !!seriesAppointmentCount && !!appointmentNumber;
  const seriesMinDays = customConfigs.scheduleAppointmentSeriesMinTime?.time;
  const seriesMaxDays = customConfigs.scheduleAppointmentSeriesMaxTime?.time;

  const providerIds = useMemo(() => {
    if (!ehrProviderInformation) return [];
    if (selectedProviderId) {
      const selected = ehrProviderInformation.find(
        (config) => config.providerUserId === selectedProviderId,
      )?.providerUserId;
      return selected ? [selected] : [];
    }
    return [];
  }, [ehrProviderInformation, selectedProviderId]);
  const practiceIds = useMemo(() => {
    if (!ehrProviderInformation) return [];
    if (selectedProviderId) {
      const selected = ehrProviderInformation
        .filter((config) => config.providerUserId === selectedProviderId)
        .map((config) => config.practiceId);
      return selected ? _.uniq(selected) : [];
    }
    return [];
  }, [ehrProviderInformation, selectedProviderId]);

  const loadAppointments = useCallback(
    async (filters: AppointmentFilters, coords?: api.Places.Geometry) => {
      const endDate =
        filters.endDay ||
        (filters.startDay && moment(filters.startDay).endOf('day').toDate());
      try {
        await verifyRequest(
          () =>
            api.client.EhrTimeSlot.fetchAvailableTimeSlots({
              previsitFormFieldId,
              startTime: filters.startDay,
              endTime: endDate,
              providerIds: providerIds.length ? providerIds : undefined,
              practiceIds: practiceIds.length ? practiceIds : undefined,
            }),
          async (
            updatedSlots: api.EhrTimeSlot.AvailableEhrTimeSlotResponse,
          ) => {
            const {
              orders,
              practices,
              providers,
              availableEhrTimeSlots,
              patientOrder,
              showConfirmation,
            } = updatedSlots;
            setAvailableSlots({
              ...updatedSlots,
              availableEhrTimeSlots: getLocallySortedSlots({
                slots: availableEhrTimeSlots,
                practices,
                providers,
                sort: filters.sort,
                coords,
                patientPcpUserId: updatedSlots.mainProviderUserId,
              }),
            });
            if (
              scheduleAppointmentDisplayBy ===
              api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
                .Provider
                ? _.uniqBy(availableEhrTimeSlots, 'providerId').length > 1
                : _.uniqBy(availableEhrTimeSlots, 'practiceId').length > 1
            ) {
              setIsMultiView(true);
            }
            if (orders) {
              setOrder(orders[Object.keys(orders)[0]]);
            }
            if (patientOrder) {
              setCurrentPatientOrder(patientOrder);
            }
            if (patientOrder?.seriesAppointmentCount) {
              setSeriesAppointmentCount(patientOrder.seriesAppointmentCount);
            }
            if (showConfirmation) {
              history.replace({
                pathname: './confirmation',
                search: history.location.search,
              });
            }
          },
          setOnCaptchaSuccess,
        );
      } catch (e) {
        setAvailableSlots({
          availableEhrTimeSlots: [],
          practices: {},
          providers: {},
        });
      }
    },
    [
      previsitFormFieldId,
      scheduleAppointmentDisplayBy,
      providerIds,
      practiceIds,
      setOnCaptchaSuccess,
      setSeriesAppointmentCount,
      history,
    ],
  );

  const updateLocallySortedSlots = useCallback(
    (filters: AppointmentFilters, coords?: api.Places.Geometry) => {
      if (!availableSlots) return;
      const { availableEhrTimeSlots, practices, providers } = availableSlots;
      if (!availableEhrTimeSlots.length) return;
      const sortedSlots = getLocallySortedSlots({
        slots: availableEhrTimeSlots,
        practices,
        providers,
        sort: filters.sort,
        coords,
        patientPcpUserId: availableSlots?.mainProviderUserId,
      });
      setAvailableSlots({
        ...availableSlots,
        availableEhrTimeSlots: sortedSlots,
      });
    },
    [availableSlots],
  );

  useEffect(() => {
    const initialLoad = async () => {
      setPage(Page.Loading);
      setIsLoading(true);
      let sortBy = await getSortBy(
        scheduleAppointmentSortBy,
        scheduleAppointmentDisplayBy,
      );
      let newCoords = coordinates;
      if (sortBy.sort === api.EhrTimeSlot.Sorting.Distance) {
        try {
          newCoords = coordinates ?? (await getCoordinates());
          if (newCoords !== coordinates && newCoords) {
            setCoordinates(newCoords);
          }
        } catch (err) {
          sortBy = errorHandleGeoLocation(scheduleAppointmentDisplayBy, t);
        }
      }
      const newFilters = {
        ...filters,
        ...sortBy,
      };
      setFilters(newFilters);
      await loadAppointments(newFilters, newCoords);
      setIsLoading(false);
    };
    if (
      [Page.Loading, Page.Confirmation].includes(page) &&
      !isSelectedAppointmentBooked(selectedAppointment)
    ) {
      initialLoad();
    } else {
      setAvailableSlots({
        availableEhrTimeSlots: [],
        practices: {},
        providers: {},
      });
      setPage(Page.Loading);
      setErrorMessage('');
    }
    return () => {
      setIsLoading(false);
      setPage(Page.Loading);
      setFilters({
        sort:
          (scheduleAppointmentDisplayBy &&
            DEFAULT_DISPLAY_BY_SORT[scheduleAppointmentDisplayBy]) ??
          DEFAULT_SORT_BY,
      });
      setAvailableSlots(undefined);
      setSelectedTimeSlot(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previsitFormFieldId, selectedAppointment, scheduleAppointmentDisplayBy]);

  useEffect(() => {
    if (isLoading && page === Page.Loading) return;
    if (page === Page.Loading && selectedAppointment) {
      if (!isSelectedAppointmentBooked(selectedAppointment)) {
        return setPage(Page.Selecting);
      } else if (!provisionalPatientId) {
        return setPage(Page.Confirmation);
      }
      return setPage(Page.Booking);
    }
    if (page === Page.Loading && !isLoading) {
      return setPage(Page.Selecting);
    }
    if (isLoading && page !== Page.Loading) {
      return setPage(Page.Loading);
    }
  }, [selectedAppointment, page, isLoading, provisionalPatientId]);

  useEffect(() => {
    const orderCreated =
      currentPatientOrder?.ehrCreated ||
      currentPatientOrder?.ehrScheduled ||
      order?.ehrCreated ||
      order?.ehrScheduled;
    if (!orderCreated) {
      setDaysAllowedFrom(
        getMaybeDaysFromTimingWithUnit(scheduleAppointmentStartTime) ||
          DEFAULT_DAYS_ALLOWED_START,
      );
      setDaysAllowedTo(
        getMaybeDaysFromTimingWithUnit(scheduleAppointmentEndTime) ||
          DEFAULT_DAYS_ALLOWED_END,
      );
    } else if (currentPatientOrder) {
      const { startDays, endDays } = retrieveReturnToClinicStartEndDays({
        orderCreated,
        followUpValue: currentPatientOrder.followUpValue,
        followUpUnit: currentPatientOrder.followUpUnit,
        scheduleAppointmentEndTime,
      });
      setDaysAllowedFrom(startDays);
      setDaysAllowedTo(endDays);
    } else if (order) {
      const { startDays, endDays } = retrieveReturnToClinicStartEndDays({
        orderCreated,
        followUpValue: order.followUpValue,
        followUpUnit: order.followUpUnit,
        scheduleAppointmentEndTime,
      });
      setDaysAllowedFrom(startDays);
      setDaysAllowedTo(endDays);
    }
  }, [
    scheduleAppointmentStartTime,
    scheduleAppointmentEndTime,
    order,
    currentPatientOrder,
  ]);

  const updateFilters = useCallback(
    async (updatedFilters: AppointmentFilters) => {
      setFilters(updatedFilters);
      let newCoords = coordinates;
      if (updatedFilters.sort === api.EhrTimeSlot.Sorting.Distance) {
        setIsLoading(true);
        try {
          newCoords = coordinates ?? (await getCoordinates());
          if (newCoords !== coordinates && newCoords) {
            setCoordinates(newCoords);
          }
        } catch (err) {
          setIsLoading(false);
          const sortBy = errorHandleGeoLocation(
            scheduleAppointmentDisplayBy,
            t,
          );
          setFilters({
            ...updatedFilters,
            ...sortBy,
          });
          return;
        }
      }
      setIsLoading(false);
      setFilters(updatedFilters);
      if (
        updatedFilters.startDay !== filters.startDay ||
        updatedFilters.endDay !== filters.endDay
      ) {
        setIsLoading(true);
        await loadAppointments(updatedFilters, newCoords);
        setIsLoading(false);
      } else {
        updateLocallySortedSlots(updatedFilters, newCoords);
      }
    },
    [
      coordinates,
      filters.endDay,
      filters.startDay,
      scheduleAppointmentDisplayBy,
      loadAppointments,
      setCoordinates,
      t,
      updateLocallySortedSlots,
    ],
  );

  const selectSlot = async (timeSlot: api.EhrTimeSlot.EhrTimeSlot) => {
    try {
      const { navigateToConfirmation } = await onAppointmentSelected(
        timeSlot,
        seriesAppointmentCount,
      );
      if (
        navigateToConfirmation &&
        isSeriesAppointmentScheduling &&
        appointmentNumber === seriesAppointmentCount
      ) {
        history.push({
          pathname: './confirmation',
          search: history.location.search,
        });
      } else if (navigateToConfirmation) {
        setPage(Page.Confirmation);
      } else {
        navigateToNextPage();
      }
    } catch (e) {
      logger.error(e, `ScheduleAppointmentPage.bookSlot`);
      setErrorMessage(
        'The appointment could not be scheduled, please select another date or time.',
      );
      setPage(Page.Selecting);
    }
  };

  const onAppointmentSelectedHandler = async (
    timeslot: api.EhrTimeSlot.EhrTimeSlot,
  ) => {
    setSelectedTimeSlot(timeslot);
    setErrorMessage('');
    setPage(Page.Booking);

    if (scheduleAppointmentCommentEnabled) {
      setPage(Page.Selecting);
      setIsCommentModalOpen(true);
      return;
    }
    await selectSlot(timeslot);
  };

  if (page === Page.Loading || !availableSlots) {
    return <Loader label={t('Fetching available time slots...')} />;
  }

  const availableEhrTimeSlots = availableSlots.availableEhrTimeSlots.filter(
    (slot) =>
      applyTimeFilters(
        slot,
        filters,
        availableSlots.practices[slot.practiceId].timezone,
      ),
  );

  const patientPcpPreferred = fieldConfig.fhirDynamicProviders?.includes(
    api.PrevisitFormTemplateFieldConfig.FhirDynamicProviderKind
      .PatientPcpPreferred,
  );

  return (
    <div
      className={classnames(
        [
          isMultiView ? styles.AppointmentFullWidth : styles.AppointmentPage,
        ].join(' '),
      )}
    >
      {errorMessage && (
        <InfoMessage className={styles.ErrorMessage} isError={true}>
          {t(errorMessage)}
        </InfoMessage>
      )}
      {page !== Page.Confirmation && isSeriesAppointmentScheduling && (
        <div>
          Appointment {appointmentNumber} of {seriesAppointmentCount}
        </div>
      )}
      {page !== Page.Confirmation &&
        page !== Page.Booking &&
        !!availableSlots?.availableEhrTimeSlots.length && (
          <div className={defaultStyles.SchedulingPrimitiveLabel}>
            {formatString(label)}
          </div>
        )}

      {page === Page.Selecting && (
        <>
          {isSeriesAppointmentScheduling && seriesMinDays && seriesMaxDays && (
            <Alert variant="info">
              {appointmentNumber === 1
                ? 'Please schedule your first appointment.'
                : `Please schedule your next appointment ${seriesMinDays} to ${seriesMaxDays} 
                    days after your previously scheduled appointment.`}
            </Alert>
          )}
          {!isSeriesAppointmentScheduling && (
            <Filters
              onFiltersChange={updateFilters}
              filters={filters}
              displayBy={scheduleAppointmentDisplayBy ?? undefined}
              daysAllowedFrom={daysAllowedFrom}
              daysAllowedTo={daysAllowedTo}
              patientPcpPreferred={patientPcpPreferred}
            />
          )}
          <ScheduleAppointment
            availableEhrTimeSlots={availableEhrTimeSlots}
            practices={availableSlots.practices}
            providers={availableSlots.providers}
            orders={availableSlots.orders}
            navigateToNextPage={navigateToNextPage}
            onAppointmentSelected={onAppointmentSelectedHandler}
            practice={practice}
            encounter={encounter}
            fieldConfig={fieldConfig}
            patientOrder={availableSlots.patientOrder}
            mainProviderUserId={availableSlots.mainProviderUserId}
            patientPcpPreferred={patientPcpPreferred}
            filterProps={{
              onFiltersChange: updateFilters,
              filters,
              displayBy: scheduleAppointmentDisplayBy ?? undefined,
              daysAllowedFrom,
              daysAllowedTo,
            }}
          />
        </>
      )}
      {page === Page.Booking && (
        <Loader label={t('Confirming your appointment...')} />
      )}
      {page === Page.Confirmation &&
        isSelectedAppointmentBooked(selectedAppointment) && (
          <ConfirmationPage
            timeSlotConfirmation={selectedAppointment}
            navigateToNextPage={navigateToNextPage}
          />
        )}
      <CommentModal
        isModalOpen={isCommentModalOpen}
        setIsModalOpen={setIsCommentModalOpen}
        bookSlot={selectSlot}
        selectedTimeSlot={selectedTimeSlot}
        characterLimit={DEFAULT_MAX_CHAR_LENGTH}
      />
    </div>
  );
};

const mapStateToProps = (state: store.All): StateProps => ({
  coordinates: state.coordinates,
  ehrProviderInformation: state.inbound.ehrProviderInformation,
  selectedProviderId: state.inbound.selectedProviderId,
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setCoordinates: (coordinates: api.Places.Geometry) =>
      dispatch(rootReducer.setCoordinates(coordinates)),
    setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) =>
      dispatch(rootReducer.setOnCaptchaSuccess(onCaptchaSuccess)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AppointmentPage);
